/* eslint-disable react-hooks/set-state-in-effect */
import { Box, RadioSelectableCard, RadioSelectableCardProps, Spacer } from '@qbe/ads-core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import Icon from '~/components-ds/Icon';
import LinkButton from '~/components-ds/LinkButton';
import Loading from '~/components-ds/Loading';
import { OCCUPATION_CATEGORY_LIMIT } from '~/constants/domain-specific';
import ERRORS from '~/constants/errors';
import { useAppContent, useAppDispatch, useBusinessDetails, useOptionSelect } from '~/hooks';
import { useRTKQErrorHandler } from '~/hooks/useRTKQErrorHandler';
import { selectIsPropertyOwner } from '~/selectors/selectBusinessDetails';
import { useGetOccupationCategoriesQuery } from '~/services/refData';
import {
  updateOccupationCategory,
  updateShowAllOccupationCategories,
} from '~/slices/businessDetails';
import { hardDecline } from '~/slices/decline';
import { qalFormStartAction, qalTrackEventAction } from '~/thunks/analytics';
import { occupationSearchTypes } from '~/types/analytics';
import { OccupationCategory, UnderwritingStatus } from '~/types/refData';
import { occupationCategoryCodeToIconList, utcDateFormatter } from '~/utils';
import { getActivePagePath } from '~/utils/analytics/helpers';
import { isFormStartStarted } from '~/utils/analytics/trackUtils';

export interface OccupationCategorySelectorProps {
  onSelect?: () => void;
}

const toOption = ({ categoryCode: id, categoryDescription: value }: OccupationCategory) => ({
  id,
  value,
});

const OccupationCategorySelector: React.FC<OccupationCategorySelectorProps> = ({ onSelect }) => {
  const { getTextContent } = useAppContent({
    basePath: 'dsme.businessDetailsOccupancySearch',
  });

  const { industry, coverStart, occupationCategory, showAllOccupationCategories } =
    useBusinessDetails();
  const [showAllLink, setShowAllLink] = useState(!!showAllOccupationCategories);
  const isPropertyOwner = useSelector(selectIsPropertyOwner);

  const { data, error, isLoading, isFetching } = useGetOccupationCategoriesQuery({
    industryCode: industry!.industryCode,
    effectiveDate: utcDateFormatter(coverStart!),
  });

  useRTKQErrorHandler(error);

  const options = useMemo(
    () =>
      data?.occupationCategories
        .slice()
        .sort((a, b) => a.groupPriority - b.groupPriority)
        .map(toOption) ?? [],
    [data]
  );

  const { selectedIDs, onSelectID, setSelectedIDs } = useOptionSelect({
    options,
  });
  const [isPreLoading, setIsPreLoading] = useState(true);

  const dispatch = useAppDispatch();

  const handleFormStart = useCallback(() => {
    if (!isFormStartStarted()) dispatch(qalFormStartAction());
  }, [dispatch]);

  const handleSelect = (id: string) => {
    onSelectID(id);
    const selectedCategory = data!.occupationCategories.find((o) => o.categoryCode === id)!;
    // @TODO Jeff 09/11/0222 - Modify dispatch logic to dispatch value to redux storey before hard decline.
    handleFormStart();
    dispatch(
      selectedIDs.includes(id)
        ? updateOccupationCategory(undefined)
        : updateOccupationCategory(selectedCategory)
    );
    if (!isPropertyOwner && selectedCategory.underwritingStatus === UnderwritingStatus.Decline) {
      dispatch(
        hardDecline({
          ...ERRORS.occupationCategory,
          searchType: occupationSearchTypes.iconSearch,
        })
      );
    } else {
      onSelect?.();
    }
  };

  const limitedOptions = showAllLink ? options : options.slice(0, OCCUPATION_CATEGORY_LIMIT);

  useEffect(() => {
    if (isLoading || isFetching) setIsPreLoading(true);
    if (data) {
      setShowAllLink(
        showAllOccupationCategories ??
          data.occupationCategories?.length <= OCCUPATION_CATEGORY_LIMIT
      );
      setTimeout(() => {
        setIsPreLoading(false);
      }, 500);
    }
    if (occupationCategory) {
      const option = options.find((o) => o?.id === toOption(occupationCategory)?.id);
      setSelectedIDs(option ? [option.id] : []);
    } else {
      setSelectedIDs([]);
    }
  }, [
    data,
    isLoading,
    isFetching,
    occupationCategory,
    setSelectedIDs,
    options,
    showAllOccupationCategories,
  ]);

  const radioList = useMemo(
    () =>
      limitedOptions.map(({ id, value }) => ({
        label: value,
        id: `occupation-category_${value}`,
        value: id,
        // TODO - Use @qbe/ui-qbe-icons
        customComponent: (
          <Icon name={occupationCategoryCodeToIconList[id] || 'other'} width={45} height={45} />
        ),
        'data-track': isPropertyOwner
          ? "Select your tenant's occupation"
          : 'Select your occupation',
        'data-track-value': 'capture',
      })) as RadioSelectableCardProps['radioList'],
    [isPropertyOwner, limitedOptions]
  );

  useEffect(() => {
    // Wait for all necessary API responses to complete before firing the event.
    // If either the data is still loading (`isLoading`) or being fetched (`isFetching`),
    // do nothing and wait for the state to resolve.
    if (isPreLoading || isLoading || isFetching) return;

    // Once the API responses are complete and the DOM has been updated with the new data,
    // dispatch the "optimize.activate" event using QAL (QBE Analytics Library).
    // This event notifies Webtrends (or similar analytics tools) that the page is ready
    // with updated dynamic content, ensuring accurate visibility tracking.
    dispatch(
      qalTrackEventAction({
        event: 'optimize.activate',
        data: {
          virtualPagePath: getActivePagePath(),
        },
      })
    );
  }, [isPreLoading, isLoading, isFetching, dispatch]);

  if (isPreLoading || isLoading || isFetching) {
    return <Loading />;
  }

  return (
    <>
      <RadioSelectableCard
        title={`${getTextContent('fields.iconSearchCategory.titlePrefix')}
          ${
            isPropertyOwner
              ? getTextContent('fields.iconSearchCategory.landlordTitle')
              : getTextContent('fields.iconSearchCategory.businessOperatorTitle')
          }`}
        direction="horizontal"
        labelPlacement="top"
        name="occupation-category"
        defaultValue={occupationCategory?.categoryCode ?? ''}
        onChange={handleSelect}
        radioList={radioList}
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        sx={{
          // TODO - https://qbe-appservices.atlassian.net/browse/AD1-462
          maxWidth: 'unset',
        }}
      />
      {!showAllLink && (
        <Box textAlign="center">
          <Spacer size="md" />
          <LinkButton
            label={getTextContent('buttons.seeAllOccupationCategories')}
            onClick={() => {
              dispatch(updateShowAllOccupationCategories(true));
            }}
            dataTrack="can not find occupation"
            sx={{ textDecoration: 'underline' }}
          />
        </Box>
      )}
    </>
  );
};

export default OccupationCategorySelector;
